import type { QueryClient } from "@tanstack/react-query";
import { cache } from "react";
import { UserProfileApi } from "@/openapi/generated";
import type { GetStudentResponse } from "@/openapi/generated";
import { QueryKeys } from "@/lib/types";
import { createAuthenticatedApi } from "@/lib/utils";

/** Deduped per-request students fetch for SSR (layout prefetch + page orchestration). */
export const fetchUserStudents = cache(
  async (
    userId: number,
    accessToken: string,
  ): Promise<GetStudentResponse[]> => {
    const service = createAuthenticatedApi(UserProfileApi, accessToken);

    try {
      return await service.apiUserProfileIdStudentsGet({ id: userId });
    } catch {
      return [];
    }
  },
);

/** Put students into the page QueryClient so HydrationBoundary can seed the client cache. */
export function seedUserStudentsQuery(
  queryClient: QueryClient,
  userId: number,
  students: GetStudentResponse[],
) {
  queryClient.setQueryData([QueryKeys.UserStudentsGet, userId], students);
}

/** Fetch (request-deduped) + seed page RQ cache for member portal SSR pages. */
export async function fetchAndSeedUserStudents(
  queryClient: QueryClient,
  userId: number,
  accessToken: string,
): Promise<GetStudentResponse[]> {
  const students = await fetchUserStudents(userId, accessToken);
  seedUserStudentsQuery(queryClient, userId, students);
  return students;
}
